import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core';
import type { FigureService } from '../figure-service';
import type { FigureAddService, FigureData } from '../figure-add-service';
import { Specification, Figure, type Annotation } from './specification';

@injectable()
export class FigureServiceImpl implements FigureService, FigureAddService {
    private _currentFigure: Figure | null;
    private _currentIndex: number = 0;
    private _onCurrentIndexChangeEmitter = new Emitter<number>();
    readonly onCurrentIndexChange: Event<number> = this._onCurrentIndexChangeEmitter.event;

    private _onFigureRenderReadyEmitter = new Emitter<void>();
    readonly onFigureRenderReady: Event<void> = this._onFigureRenderReadyEmitter.event;

    private _onCaptureFigureEmitter = new Emitter<void>();
    readonly onCaptureFigure: Event<void> = this._onCaptureFigureEmitter.event;

    constructor(
        @inject(Specification)
        private _specification: Specification,
    ) {
        const figures = this._specification.getAllFigures();
        this._currentFigure = figures.length > 0 ? figures[0] : null;
        this._currentIndex = 0;
    }

    setCurrentIndex(index: number): void {
        if (index !== this._currentIndex) {
            this._currentIndex = index;
            const figures = this._specification.getAllFigures();
            this._currentFigure = figures[index] || null;
            this._onCurrentIndexChangeEmitter.fire(this._currentIndex);
        }
    }

    getCurrentIndex(): number {
        return this._currentIndex;
    }

    getCurrentFigure(): Figure | null {
        return this._currentFigure;
    }

    getCurrentFigureNumber(): number {
        return this._currentFigure ? this._currentFigure.getOrder() : 0;
    }

    hasNextFigure(): boolean {
        if (!this._currentFigure) return false;
        const figures = this._specification.getAllFigures();
        const currentIndex = figures.findIndex((f: Figure) => f.getId() === this._currentFigure?.getId());
        return currentIndex < figures.length - 1;
    }

    hasPreviousFigure(): boolean {
        if (!this._currentFigure) return false;
        const figures = this._specification.getAllFigures();
        const currentIndex = figures.findIndex((f: Figure) => f.getId() === this._currentFigure?.getId());
        return currentIndex > 0;
    }

    moveToNextFigure(): void {
        if (this.hasNextFigure()) {
            const figures = this._specification.getAllFigures();
            const currentIndex = figures.findIndex((f: Figure) => f.getId() === this._currentFigure?.getId());
            this._currentFigure = figures[currentIndex + 1];
        }
    }

    moveToPreviousFigure(): void {
        if (this.hasPreviousFigure()) {
            const figures = this._specification.getAllFigures();
            const currentIndex = figures.findIndex((f: Figure) => f.getId() === this._currentFigure?.getId());
            this._currentFigure = figures[currentIndex - 1];
        }
    }

    getTotalFigures(): number {
        return this._specification.getAllFigures().length;
    }

    getCurrentFigureAnnotations(): Annotation[] {
        return this._currentFigure ? this._currentFigure.annotations : [];
    }

    findAnnotationById(annotationId: string): Annotation | undefined {
        const figures = this._specification.getAllFigures();
        for (const figure of figures) {
            const annotation = figure.annotations.find((a: Annotation) => a.getId() === annotationId);
            if (annotation) return annotation;
        }
        return undefined;
    }

    notifyFigureRenderReady(): void {
        this._onFigureRenderReadyEmitter.fire();
    }

    async requestCaptureAll(): Promise<void> {
        const figures = this._specification.getAllFigures();
        if (figures.length === 0) return;

        for (let i = 0; i < figures.length; i++) {
            const isCurrentIndex = this._currentIndex === i;
            await new Promise<void>((resolve) => {
                const renderDispose = this.onFigureRenderReady(() => {
                    renderDispose.dispose();
                    resolve();
                });

                if (isCurrentIndex) {
                    // 使用 setTimeout 确保事件监听器已经注册
                    setTimeout(() => {
                        this._onFigureRenderReadyEmitter.fire();
                    }, 0);
                } else {
                    this.setCurrentIndex(i);
                }
            });
        }
    }

    addFigures(urls: FigureData[]): void {
        for (const { url, s3Key, uniqueId } of urls) {
            const figuresCount = this._specification.getAllFigures().length;
            const order = figuresCount + 1;
            const figure = new Figure({
                id: uniqueId,
                order,
                url,
                title: `Fig.${order}`,
            });
            figure.setS3Key(s3Key);
            if (figuresCount === 0) {
                figure.setAbstract(true);
            }
            this._specification.addFigure(figure);
        }
    }
}
